import { useCallback, useRef, useState } from 'react';
import type { MouseEvent } from 'react';
import { useLocale } from './useLocale';
import {
  consumePendingNonFarsiContentHint,
  consumeSkipNonFarsiContentHintOnce,
  isDesignedForNonFarsiUsers,
  isNonFarsiContentHintSuppressed,
  clearNonFarsiContentHintSuppressed,
  setNonFarsiContentHintSuppressed,
  setPendingNonFarsiContentHint,
  type NonFarsiContentKind,
  nonFarsiContentKey,
} from '../utils/nonFarsiContentHint';

export type AnchorRect = {
  top: number;
  left: number;
  right: number;
  width: number;
};

export type NonFarsiGateState = {
  open: boolean;
  anchorRect: AnchorRect | null;
  contentKey: string;
};

type GateItem = {
  isDesignedForNonFarsiUsers?: boolean;
  IsDesignedForNonFarsiUsers?: boolean;
};

export type NonFarsiGateOptions = {
  /** Hint is deferred until after navigation (destination calls tryOpenPendingHint). */
  navigatesAway?: boolean;
};

export function useNonFarsiContentGate() {
  const { locale, direction } = useLocale();
  const isRtl = direction === 'rtl';
  const contentKeyRef = useRef('');
  const [state, setState] = useState<NonFarsiGateState>({
    open: false,
    anchorRect: null,
    contentKey: '',
  });

  const shouldGate = useCallback(
    (item: GateItem | null | undefined, _kind: NonFarsiContentKind, _id: number) => {
      if (locale === 'en') return false;
      if (!isDesignedForNonFarsiUsers(item)) return false;
      return !isNonFarsiContentHintSuppressed();
    },
    [locale]
  );

  const showHintPanel = useCallback(
    (contentKey: string) => {
      if (locale === 'en') return;
      contentKeyRef.current = contentKey;
      const openPanel = () => {
        setState({
          open: true,
          anchorRect: null,
          contentKey,
        });
      };
      requestAnimationFrame(() => {
        requestAnimationFrame(openPanel);
      });
    },
    [locale]
  );

  /** Opens the hint even when "do not show again" is checked (icon click). Non-English UI only. */
  const openHintManually = useCallback(
    (kind: NonFarsiContentKind, id: number) => {
      if (locale === 'en') return;
      showHintPanel(nonFarsiContentKey(kind, id));
    },
    [locale, showHintPanel]
  );

  const openGate = useCallback(
    (
      _anchorEl: HTMLElement | null,
      kind: NonFarsiContentKind,
      id: number,
      action: () => void | Promise<void>,
      options?: NonFarsiGateOptions
    ) => {
      const contentKey = nonFarsiContentKey(kind, id);

      if (options?.navigatesAway) {
        action();
        setPendingNonFarsiContentHint(contentKey);
        return;
      }

      void Promise.resolve(action()).then(() => {
        showHintPanel(contentKey);
      });
    },
    [showHintPanel]
  );

  const tryOpenPendingHint = useCallback(
    (
      item: GateItem | null | undefined,
      kind: NonFarsiContentKind,
      id: number,
      _options?: NonFarsiGateOptions
    ) => {
      const contentKey = nonFarsiContentKey(kind, id);
      if (consumeSkipNonFarsiContentHintOnce(contentKey)) return;
      consumePendingNonFarsiContentHint(contentKey);
      if (!shouldGate(item, kind, id)) return;
      showHintPanel(contentKey);
    },
    [shouldGate, showHintPanel]
  );

  const gateClick = useCallback(
    (
      event: MouseEvent<HTMLElement>,
      item: GateItem | null | undefined,
      kind: NonFarsiContentKind,
      id: number,
      action: () => void | Promise<void>,
      options?: NonFarsiGateOptions
    ) => {
      void event;
      if (!shouldGate(item, kind, id)) {
        void Promise.resolve(action());
        return;
      }
      openGate(null, kind, id, action, options);
    },
    [openGate, shouldGate]
  );

  const gateWithoutClick = useCallback(
    (
      anchorEl: HTMLElement | null,
      item: GateItem | null | undefined,
      kind: NonFarsiContentKind,
      id: number,
      action: () => void | Promise<void>,
      options?: NonFarsiGateOptions
    ) => {
      if (!shouldGate(item, kind, id)) {
        void Promise.resolve(action());
        return;
      }
      openGate(anchorEl, kind, id, action, options);
    },
    [openGate, shouldGate]
  );

  const closeGate = useCallback(() => {
    setState((s) => ({ ...s, open: false, anchorRect: null }));
  }, []);

  const handleDismiss = useCallback(() => {
    closeGate();
  }, [closeGate]);

  const handleContinue = useCallback(
    (dontShowAgain: boolean) => {
      if (dontShowAgain) {
        setNonFarsiContentHintSuppressed();
      } else {
        clearNonFarsiContentHintSuppressed();
      }
      closeGate();
    },
    [closeGate]
  );

  return {
    isRtl,
    gateState: state,
    shouldGate,
    gateClick,
    gateWithoutClick,
    tryOpenPendingHint,
    openHintManually,
    handleDismiss,
    handleContinue,
  };
}
